import { CheckBox, Tenants } from '@zappyrent/hundop';
import { upperFirst } from 'lodash';
import { Info } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import Error from '../../components/Error';
import Footer from '../../components/Footer';
import NavBar from '../../components/NavBar';
import AddAGuarantor from '../../components/UploadDocuments/AddAGuarantor';
import AddALandlordReferral from '../../components/UploadDocuments/AddALandlordReferral';
import { TenantLandlordReferralWebpageSource } from '../../components/UploadDocuments/AddALandlordReferral/constant/tenant-landlord-referral-field.contant';
import AddATenant from '../../components/UploadDocuments/AddATenant';
import {
  ButtonContainer,
  StyledContinueButton,
} from '../../components/UploadDocuments/AddATenant/AddATenant.styles';
import SendToAnalysisModal from '../../components/UploadDocuments/SendToAnalysisModal';
import { USERS_TYPES } from '../../shared/constants';
import { areGuarantorDocumentsUploaded } from '../../shared/helpers';
import { areGuestDocumentsUploaded } from '../../shared/helpers/areGuestsDocumentsUploaded';
import { tenantsFieldAreValid } from '../../shared/helpers/tenantsFieldAreValid';
import { useUser } from '../../shared/hooks';
import useBookingData from '../../shared/hooks/useBookingData';
import useBookingGuarantors from '../../shared/hooks/useBookingGuarantors';
import useBookingGuests from '../../shared/hooks/useBookingGuestData';
import { IGuarantor } from '../../shared/ts/guarantor';
import { IGuest } from '../../shared/ts/user';
import useStore from '../../store';
import { StyledSubSection } from '../ThankYou/ThankYou.styles';
import {
  StyledAddPaymentCta,
  StyledAddPaymentCtaIcon,
  StyledAddPaymentCtaLabel,
  StyledBox,
  StyledGrid,
  StyledH2,
  StyledSectionContainer,
  StyledText,
  StyledSubSectionRow,
  StyledMissingFieldLabelsContainer,
  StyledMissingFieldLabel,
  StyledMissingFieldH4,
  StyledMissingFieldTitle,
  StyledWarningWrapper,
  StyledWarningText,
} from './AttachDocuments.styles';
import {
  IMissingFormRequiredFieldGuarantor,
  IMissingFormRequiredFieldTenant,
} from './AttachDocuments.types';

const AttachDocuments = (): JSX.Element | null => {
  const { t } = useTranslation([
    'documentsflow',
    'a11y',
    'errors',
    'payments',
    'booking',
    'navigation',
  ]);

  const [searchParams] = useSearchParams();
  const isLoadingUser = useUser();
  const bookingId = searchParams.get('booking_id');

  if (!bookingId)
    return (
      <Error errorMessage={upperFirst(t('errors:errorWithoutBookingId'))} />
    );

  const isGettingGuestInfo = useBookingGuests(bookingId);
  const bookingData = useBookingData(bookingId);
  const isLoadingGuarantors = useBookingGuarantors(bookingId, true);
  const { guests, addedGuarantor, user } = useStore(state => ({
    guests: state.guests,
    addedGuarantor: state.guarantors,
    user: state.user,
  }));

  const [tenants, setTenants] = useState<IGuest[]>([]);

  useEffect(() => {
    const mainTenant = guests.find(
      guest => guest.user_id === bookingData?.user_id,
    );
    const otherGuests = guests.filter(
      guest => guest.user_id !== bookingData?.user_id,
    );

    const guarantorUserIds = new Set(
      addedGuarantor.map(guarantor => Number(guarantor.user_id)),
    );
    const filteredGuests = otherGuests.filter(
      guest => !guarantorUserIds.has(Number(guest.user_id)),
    );

    const uniqueTenants = new Set();
    if (mainTenant) {
      uniqueTenants.add(mainTenant);
    }
    filteredGuests.forEach(guest => uniqueTenants.add(guest));

    setTenants(Array.from(uniqueTenants) as IGuest[]);
  }, [guests, addedGuarantor, bookingData]);

  useEffect(() => {
    setGuarantors(
      addedGuarantor.filter(
        guarantor =>
          Number(bookingData?.user_id ?? guests[0]?.user_id) !==
          Number(guarantor.user_id),
      ),
    );
  }, [addedGuarantor, bookingData?.user_id, guests]);

  const handleAddTenant = () => {
    setTenants(prevTenants => [...prevTenants, {} as IGuest]);
  };

  const [guarantors, setGuarantors] = useState<IGuarantor[]>([]);
  const [canGuarantee, setCanGuarantee] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [areTOSAccepted, setIsTOSAccepted] = useState(false);
  const [misingFormRequiredFieldTenant, setMisingFormRequiredFieldTenant] =
    useState<IMissingFormRequiredFieldTenant[]>([]);
  const [misingFormRequiredFieldGurantor, setMisingFormRequiredFieldGurantor] =
    useState<IMissingFormRequiredFieldGuarantor[]>([]);

  const showMissingInfoTitle = useMemo(() => {
    const combinedFields = [
      ...misingFormRequiredFieldTenant,
      ...misingFormRequiredFieldGurantor,
    ];
    return combinedFields.some(
      item =>
        (item?.documents && item.documents?.fields?.length > 0) ||
        item.fields.length > 0,
    );
  }, [misingFormRequiredFieldGurantor, misingFormRequiredFieldTenant]);

  const handleAddGuarantor = () => {
    setGuarantors(prevGuarantors => [...prevGuarantors, {} as IGuarantor]);
  };
  const handleGuarantorDelete = (index: number) => {
    setGuarantors(prevGuarantors => {
      const updatedGuarantors = [...prevGuarantors];
      updatedGuarantors.splice(index, 1);
      return updatedGuarantors;
    });
    setMisingFormRequiredFieldGurantor(prevFields => {
      const updatedFields = [...prevFields];
      updatedFields.splice(index, 1);
      return updatedFields;
    });
  };

  const deleteMisingFormRequiredFieldGurantor = (index: number) => {
    setMisingFormRequiredFieldGurantor(prevFields => {
      const updatedFields = [...prevFields];
      updatedFields.splice(index, 1);
      return updatedFields;
    });
  };

  useEffect(() => {
    if (bookingData && bookingData.documents_uploaded === true) {
      window.location.href = process.env.REACT_APP_SITE_URL ?? '/';
    }
  }, [bookingData]);

  const areAllGuestDocumentsUploaded = tenants.every((guest: IGuest) =>
    areGuestDocumentsUploaded(guest),
  );

  const areAllDocumentsUploadedGuarantor = guarantors.every(
    (guarantor: IGuarantor) =>
      areGuarantorDocumentsUploaded(
        guarantor!,
        guarantor?.isGuarantorGuest ?? false,
      ),
  );

  const isTenantFieldsAreValid = tenantsFieldAreValid(guests);

  const canShowSendToAnalysis =
    areAllGuestDocumentsUploaded &&
    isTenantFieldsAreValid &&
    guests.length > 0 &&
    ((guarantors.length > 0 && areAllDocumentsUploadedGuarantor) ||
      canGuarantee) &&
    areTOSAccepted;

  const sendToAnalysis = () => {
    setIsModalOpen(true);
  };
  const handleGuaranteeChange = (tenantCanGurantee: boolean) => {
    setCanGuarantee(tenantCanGurantee);
  };
  const handleDeleteTenant = (index: number) => {
    setTenants(prevTenants => {
      const updatedTenants = [...prevTenants];
      updatedTenants.splice(index, 1);
      return updatedTenants;
    });
  };

  const deleteMisingFormRequiredFieldTenant = (index: number) => {
    setMisingFormRequiredFieldTenant(prevFields => {
      const updatedFields = [...prevFields];
      updatedFields.splice(index, 1);
      return updatedFields;
    });
  };

  const handleMissingFormRequiredFieldTenant = (
    index: number,
    missingFields: IMissingFormRequiredFieldTenant,
  ) => {
    setMisingFormRequiredFieldTenant(prevFields => {
      const updatedFields = [...prevFields];
      updatedFields[index] = missingFields;
      return updatedFields;
    });
  };

  const handleMissingFormRequiredFieldGurantor = (
    index: number,
    missingFields: IMissingFormRequiredFieldGuarantor,
  ) => {
    setMisingFormRequiredFieldGurantor(prevFields => {
      const updatedFields = [...prevFields];
      updatedFields[index] = missingFields;
      return updatedFields;
    });
  };

  const gurantorRequiredForOccupation = [
    USERS_TYPES.ITALIAN_STUDENT,
    USERS_TYPES.FOREIGN_STUDENT,
    USERS_TYPES.SELF_EMPLOYED,
    USERS_TYPES.UNEMPLOYED,
  ];

  const isGurantorAddedForOccupation = useMemo(() => {
    if (
      tenants.some(tenant =>
        gurantorRequiredForOccupation?.includes(
          (tenant?.user_occupation || tenant?.occupation) as string,
        ),
      )
    ) {
      return !guarantors.length;
    }
    return false;
  }, [tenants, guarantors]);

  useEffect(() => {
    if (
      tenants.some(tenant =>
        gurantorRequiredForOccupation?.includes(
          (tenant?.user_occupation || tenant?.occupation) as string,
        ),
      ) &&
      guarantors.length === 0
    ) {
      setGuarantors([{} as IGuarantor]);
    }
  }, [tenants]);

  if (isLoadingUser) return null;

  return (
    <>
      <NavBar stepper={false} route_name="attachDocuments" />
      <StyledSectionContainer>
        <StyledGrid container>
          <StyledSubSection>
            <StyledBox>
              <StyledSubSection>
                <StyledH2>
                  {upperFirst(t('documentsflow:tenantPersonalInfo'))}
                </StyledH2>
                <StyledWarningWrapper>
                  <Info width="30px" height="auto" color="#FF7400" />
                  <StyledWarningText>
                    <Trans
                      components={{
                        bold: <strong />,
                      }}
                      i18nKey="translation:tenantPersonalInfoWarning"
                      t={t}
                    />
                  </StyledWarningText>
                </StyledWarningWrapper>
                <ul>
                  <li>
                    <StyledText black>
                      {upperFirst(
                        t('documentsflow:warningAnticipateInformation'),
                      )}
                    </StyledText>
                  </li>
                  <li>
                    <StyledText black>
                      {upperFirst(t('documentsflow:warningSumMonthlyIncome'))}
                    </StyledText>
                  </li>
                  <li>
                    <StyledText black>
                      {upperFirst(t('documentsflow:warningAddAll'))}
                    </StyledText>
                  </li>
                </ul>
                {isGettingGuestInfo && !isLoadingGuarantors && (
                  <StyledBox>
                    <StyledSubSection>
                      {showMissingInfoTitle && (
                        <StyledMissingFieldTitle>
                          {upperFirst(t('documentsflow:missingInfo'))}:
                        </StyledMissingFieldTitle>
                      )}
                      {misingFormRequiredFieldTenant.map((field, index) => (
                        <>
                          {field.fields.length > 0 && (
                            <StyledMissingFieldLabelsContainer
                              key={`missing-tenant-field-${index}`}
                            >
                              <StyledMissingFieldH4>
                                {upperFirst(t(field.label))}:
                              </StyledMissingFieldH4>
                              {field.fields.map((field, index) => (
                                <StyledMissingFieldLabel
                                  key={`missing-tenant-field-label-${index}`}
                                >
                                  {upperFirst(t(field))}
                                </StyledMissingFieldLabel>
                              ))}
                            </StyledMissingFieldLabelsContainer>
                          )}

                          {field?.documents &&
                            field?.documents?.fields.length > 0 && (
                              <StyledMissingFieldLabelsContainer>
                                <StyledMissingFieldH4>
                                  {upperFirst(t(field.documents.label))}:
                                </StyledMissingFieldH4>
                                {field?.documents?.fields.map(
                                  (field, index) => (
                                    <StyledMissingFieldLabel
                                      key={`missing-tenant-field-document-label-${index}`}
                                    >
                                      {field?.sideLabel
                                        ? `${upperFirst(
                                            t(field.label),
                                          )}/${upperFirst(t(field?.sideLabel))}`
                                        : upperFirst(t(field.label))}
                                    </StyledMissingFieldLabel>
                                  ),
                                )}
                              </StyledMissingFieldLabelsContainer>
                            )}
                        </>
                      ))}
                      {misingFormRequiredFieldGurantor.map((field, index) => (
                        <>
                          {field.fields.length > 0 && (
                            <StyledMissingFieldLabelsContainer
                              key={`missing-guarantor-field-${index}`}
                            >
                              <StyledMissingFieldH4>
                                {upperFirst(t(field.label))}:
                              </StyledMissingFieldH4>
                              {field.fields.map((field, index) => (
                                <StyledMissingFieldLabel
                                  key={`missing-guarantor-field-label-${index}`}
                                >
                                  {upperFirst(t(field))}
                                </StyledMissingFieldLabel>
                              ))}
                            </StyledMissingFieldLabelsContainer>
                          )}

                          {field?.documents &&
                            field?.documents?.fields.length > 0 && (
                              <StyledMissingFieldLabelsContainer>
                                <StyledMissingFieldH4>
                                  {upperFirst(t(field.documents.label))}:
                                </StyledMissingFieldH4>
                                {field?.documents?.fields.map(
                                  (field, index) => (
                                    <StyledMissingFieldLabel
                                      key={`missing-guarantor-field-document-label-${index}`}
                                    >
                                      {field?.sideLabel
                                        ? `${upperFirst(
                                            t(field.label),
                                          )}/${upperFirst(t(field?.sideLabel))}`
                                        : upperFirst(t(field.label))}
                                    </StyledMissingFieldLabel>
                                  ),
                                )}
                              </StyledMissingFieldLabelsContainer>
                            )}
                        </>
                      ))}
                    </StyledSubSection>
                    {tenants.length === 0 && (
                      <StyledSubSection>
                        <AddATenant
                          index={0}
                          guest={{}}
                          key={tenants.length}
                          onGuaranteeChange={handleGuaranteeChange}
                          handleMissingFormRequiredFieldTenant={
                            handleMissingFormRequiredFieldTenant
                          }
                          handleDeleteTenantChange={handleDeleteTenant}
                          deleteMisingFormRequiredFieldTenant={
                            deleteMisingFormRequiredFieldTenant
                          }
                        />
                      </StyledSubSection>
                    )}
                    {tenants.map((guest: IGuest, index: number) => (
                      <StyledSubSection key={guest.id ?? index}>
                        <AddATenant
                          index={index}
                          key={guest.id ?? index}
                          guest={guest}
                          onGuaranteeChange={handleGuaranteeChange}
                          handleMissingFormRequiredFieldTenant={
                            handleMissingFormRequiredFieldTenant
                          }
                          handleDeleteTenantChange={handleDeleteTenant}
                          deleteMisingFormRequiredFieldTenant={
                            deleteMisingFormRequiredFieldTenant
                          }
                        />
                      </StyledSubSection>
                    ))}
                    <StyledSubSection>
                      <StyledH2>
                        {upperFirst(t('documentsflow:addANewTenant'))}
                      </StyledH2>
                      <StyledAddPaymentCta
                        onClick={handleAddTenant}
                        title={t('documentsflow:addANewTenant')}
                      >
                        <StyledAddPaymentCtaIcon>
                          <Tenants />
                        </StyledAddPaymentCtaIcon>
                        <StyledAddPaymentCtaLabel>
                          {t('documentsflow:addANewTenant')}
                        </StyledAddPaymentCtaLabel>
                      </StyledAddPaymentCta>
                    </StyledSubSection>
                    <br />
                    {guarantors.map((guarantor, index) => (
                      <StyledSubSection
                        key={`guarantor-${guarantor.id || index}`}
                      >
                        <AddAGuarantor
                          index={index}
                          key={`guarantor-${guarantor.id || index}`}
                          guarantor={guarantor}
                          handleMissingFormRequiredFieldGurantor={
                            handleMissingFormRequiredFieldGurantor
                          }
                          handleGuarantorDelete={handleGuarantorDelete}
                          deleteMisingFormRequiredFieldGurantor={
                            deleteMisingFormRequiredFieldGurantor
                          }
                          shouldDeleteGurantorForOccupation={tenants.some(
                            tenant =>
                              gurantorRequiredForOccupation.includes(
                                tenant?.user_occupation as string,
                              ),
                          )}
                        />
                      </StyledSubSection>
                    ))}
                    <StyledSubSection>
                      <StyledH2>
                        {upperFirst(t('documentsflow:addANewGuarantor'))}
                      </StyledH2>
                      <StyledAddPaymentCta
                        onClick={handleAddGuarantor}
                        title={t('documentsflow:addANewGuarantor')}
                      >
                        <StyledAddPaymentCtaIcon>
                          <Tenants />
                        </StyledAddPaymentCtaIcon>
                        <StyledAddPaymentCtaLabel>
                          {t('documentsflow:addANewGuarantor')}
                        </StyledAddPaymentCtaLabel>
                      </StyledAddPaymentCta>
                    </StyledSubSection>
                    <br />

                    <AddALandlordReferral
                      booking_id={bookingId}
                      user_id={user?.id as number}
                      referralInfo={upperFirst(
                        t('documentsflow:addANewReferralInfo'),
                      )}
                      webpage_source={
                        TenantLandlordReferralWebpageSource.TENANT_DOCUMENT
                      }
                    />
                    <StyledSubSectionRow>
                      <CheckBox
                        id="terms_and_conditions"
                        name="terms_and_conditions"
                        label={''}
                        checked={areTOSAccepted}
                        onChange={() => setIsTOSAccepted(!areTOSAccepted)}
                        user={'tenant'}
                      />
                      <br />
                      <Trans
                        i18nKey="documentsflow:termsAndConditions"
                        components={{
                          1: (
                            <a
                              style={{
                                fontWeight: 900,
                                fontSize: '1rem',
                                color: '#0C9D9D',
                                cursor: 'pointer',
                                marginTop: '21px',
                                textDecoration: 'none',
                              }}
                              href={t('navigation:termsAndConditionsLink')}
                              target="_blank"
                              rel="noreferrer"
                            />
                          ),
                        }}
                      />
                    </StyledSubSectionRow>
                    <ButtonContainer>
                      <StyledContinueButton
                        disabled={
                          !canShowSendToAnalysis || isGurantorAddedForOccupation
                        }
                        onClick={sendToAnalysis}
                        title={t('documentsflow:sendToAnalysis')}
                      >
                        {t('documentsflow:sendToAnalysis')}
                      </StyledContinueButton>
                    </ButtonContainer>
                  </StyledBox>
                )}
              </StyledSubSection>
            </StyledBox>
          </StyledSubSection>
          <StyledSectionContainer></StyledSectionContainer>
        </StyledGrid>{' '}
      </StyledSectionContainer>
      <Footer />
      {isModalOpen && (
        <SendToAnalysisModal
          onClose={() => setIsModalOpen(false)}
          bookingId={bookingId}
          booking={bookingData}
        />
      )}
    </>
  );
};

export default AttachDocuments;
